import { FC, useEffect, useRef } from "react";

import { useIsDashboardScreenshotMode } from "stores/dashboardScreenshot/useIsDashboardScreenshotMode";
import styled from "styled-components/macro";

import { useScreenshotDispatch } from "components/screenshot/hooks";
import { useScreenshotContext } from "components/screenshot/hooks/useScreenshotContext";

import { Portal } from "../../ui";
import { useChartDispatch, useChartState } from "./context";
import { ChartLegend } from "./legend";

type ScreenshotT = {
  containerId: string;
};

const Screenshot: FC<ScreenshotT> = ({ containerId }) => {
  // context
  const { id, screenshot, request, response, options } = useChartState();
  const { settings, widget } = useScreenshotContext();

  // dispatch
  const dispatch = useChartDispatch();
  const screenshotDispatch = useScreenshotDispatch();

  // ref
  const overlayRef = useRef<HTMLDivElement>(null);
  // Guard against render loop: skip redundant dispatches when settings
  // reference changes but values are identical.
  const lastDispatchKeyRef = useRef<string>("");

  // derived state
  const screenshotContainer = document.getElementById(containerId);
  const isIndividualScreenshot = !!id && widget?.widgetId === id;
  const isDashboardMode = useIsDashboardScreenshotMode(id);
  const screenshotActive = isIndividualScreenshot || isDashboardMode;

  useEffect(() => {
    const dispatchKey = `${screenshotActive}:${JSON.stringify(settings)}`;
    if (dispatchKey === lastDispatchKeyRef.current) return;
    lastDispatchKeyRef.current = dispatchKey;

    const next = { visible: screenshotActive, preset: settings };
    dispatch({ type: "screenshot", payload: next });

    // Only go fullscreen for individual screenshot, not dashboard
    if (!isDashboardMode) {
      dispatch({ type: "fullscreen", payload: isIndividualScreenshot });
    }
  }, [dispatch, id, settings, widget, isDashboardMode]);

  // Update the widgets state (only for individual screenshot).
  useEffect(() => {
    if (isIndividualScreenshot) {
      // Copy the options to prevent mutation.
      const optionsCopy = JSON.parse(JSON.stringify(options));
      screenshotDispatch({
        payload: {
          widgetState: {
            request,
            response,
            options: optionsCopy
          }
        }
      });
    }
  }, [request, response, options, screenshotDispatch, id, widget]);

  // Don't render overlay in dashboard mode
  if (!screenshot.visible || !screenshotContainer || isDashboardMode) return null;

  // Update the overlay.
  screenshotDispatch({
    payload: {
      screenshotOverlay: overlayRef.current
    }
  });

  const screenshotBounds = {
    width: settings?.width || 1152,
    height: settings?.height || 681
  };

  return (
    <Portal container={screenshotContainer}>
      <Overlay
        className="screenshot-overlay"
        ref={overlayRef}
        width={screenshotBounds.width}
        height={screenshotBounds.height}>
        {settings?.legendVisible && (
          <ChartLegend
            parentDimensions={screenshotBounds}
            id={containerId + "chart-legend"}
            disableDragging={false}
            showLegendBorder={!!settings?.legendBorderVisible}
            width={250}
          />
        )}
      </Overlay>
    </Portal>
  );
};

export default Screenshot;

const Overlay = styled.div`
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
  border: 2px solid rgb(255, 208, 0);
  z-index: 1;
  pointer-events: none;
`;
